// src/components/atomic-crm/sales/SalesInputs.tsx
import { useTranslate, useGetList } from "ra-core";
import { useFormContext, useWatch } from "react-hook-form";
import { Input } from "@/components/ds/ui/input";
import { Label } from "@/components/ds/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ds/ui/select";
import { Switch } from "@/components/ds/ui/switch";
import { Skeleton } from "@/components/ds/ui/skeleton";

export const SalesInputs = () => {
  const translate = useTranslate();
  const { register, setValue, control, watch } = useFormContext();
  const currentRoleId = watch("role_id");
  const currentManagerId = watch("manager_id");

  // Fetch roles
  const { data: rolesRaw, isLoading: rolesLoading } = useGetList(
    "config_roles",
    {
      pagination: { page: 1, perPage: 100 },
      sort: { field: "sort_order", order: "ASC" },
      filter: { is_active: true },
    },
  );

  const roles = Array.isArray(rolesRaw) ? rolesRaw : rolesRaw?.data || [];

  // Fetch all users (filter client-side)
  const { data: usersRaw, isLoading: usersLoading } = useGetList("sales", {
    pagination: { page: 1, perPage: 100 },
    sort: { field: "first_name", order: "ASC" },
  });

  const allUsers = Array.isArray(usersRaw) ? usersRaw : usersRaw?.data || [];

  // ✅ Add this — the currently selected role in the form
  const currentRole = roles.find((r: any) => r.id === currentRoleId);

  // ✅ Replace the managers filter
  const managers = allUsers.filter((u: any) => {
    const uRole = roles.find((r: any) => r.id === u.role_id);
    if (!uRole || !uRole.can_manage_team) return false;
    if (!currentRole) return true;
    return uRole.level < currentRole.level; // strictly higher up
  });

  if (rolesLoading || usersLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-10 w-full" />
      </div>
    );
  }

  // ✅ Helper — always mark dirty/valid/touch when setting a Select value
  const setSelectValue = (field: string, value: any) => {
    setValue(field, value, {
      shouldDirty: true,
      shouldValidate: true,
      shouldTouch: true,
    });
  };

  return (
    <div className="space-y-4">
      {/* First Name */}
      <div className="space-y-2">
        <Label htmlFor="first_name">
          {translate("crm.user.field.first_name")} *
        </Label>
        <Input
          id="first_name"
          {...register("first_name", { required: true })}
        />
      </div>

      {/* Last Name */}
      <div className="space-y-2">
        <Label htmlFor="last_name">
          {translate("crm.user.field.last_name")} *
        </Label>
        <Input id="last_name" {...register("last_name", { required: true })} />
      </div>

      {/* Email */}
      <div className="space-y-2">
        <Label htmlFor="email">Email *</Label>
        <Input
          id="email"
          type="email"
          {...register("email", { required: true })}
        />
      </div>

      {/* Role Selection */}
      <div className="space-y-2">
        <Label>Role</Label>
        <Select
          value={currentRoleId?.toString() || ""}
          onValueChange={(value) => setSelectValue("role_id", parseInt(value))}
        >
          <SelectTrigger>
            <SelectValue placeholder="Select role..." />
          </SelectTrigger>
          <SelectContent>
            {roles.length === 0 ? (
              <div className="p-2 text-sm text-muted-foreground">
                No roles available
              </div>
            ) : (
              roles.map((role: any) => (
                <SelectItem key={role.id} value={role.id.toString()}>
                  {role.display_name}
                  {role.is_admin ? " (Admin)" : ""}
                </SelectItem>
              ))
            )}
          </SelectContent>
        </Select>
        <p className="text-xs text-muted-foreground">
          Admin: Full access • Manager: Team access • User: Own access only
        </p>
      </div>

      {/* Manager Selection */}
      {currentRoleId &&
        !roles.find((r: any) => r.id === currentRoleId)?.is_admin && (
          <div className="space-y-2">
            <Label>Manager</Label>
            <Select
              value={currentManagerId?.toString() || ""}
              onValueChange={(value) =>
                setSelectValue("manager_id", parseInt(value))
              }
            >
              <SelectTrigger>
                <SelectValue placeholder="Select manager..." />
              </SelectTrigger>
              <SelectContent>
                {managers.length === 0 ? (
                  <div className="p-2 text-sm text-muted-foreground">
                    No managers available
                  </div>
                ) : (
                  managers.map((manager: any) => (
                    <SelectItem key={manager.id} value={manager.id.toString()}>
                      {manager.first_name} {manager.last_name}
                    </SelectItem>
                  ))
                )}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              Optional: Assign a manager for this user
            </p>
          </div>
        )}

      {/* Legacy Admin Flag */}
      <div className="flex items-center space-x-2 pt-2">
        <Switch id="administrator" disabled {...register("administrator")} />
        <Label htmlFor="administrator">Legacy Admin Flag</Label>
      </div>
    </div>
  );
};